import {type WBSlateElementType} from '../../components/Slate/WBSlate/types';
import * as Filter from '../filters';
import type * as FilterTypes from '../filterTypes';
import {
  type FilterV2Root,
  isFilterV2,
  migrateLegacyFiltersToV2,
} from '../filterV2';
import type * as RunTypes from '../runTypes';
import {type SlateReport} from './types';

export interface MigrateReportRunSetFiltersResult {
  spec: SlateReport;
  isFiltersMigrated: boolean;
}

/**
 * Migrate run set filters in a Slate report from legacy format to FilterV2.
 *
 * When the AND/OR feature flag is enabled and a report contains run sets with
 * legacy (v1) filters, those filters are converted to the flat FilterV2 format.
 * Conversely, when the flag is off and v2 filters are present, they are reset
 * to the legacy empty state.
 *
 * Mirrors the traversal pattern in migrateReportRunSetProjects: top-level
 * blocks and recursively nested collapsedChildren are both handled.
 */
export function migrateReportRunSetFilters(
  spec: SlateReport,
  andOrEnabled: boolean
): MigrateReportRunSetFiltersResult {
  let shouldUpdateSpec = false;

  function migrateBlock(block: WBSlateElementType): WBSlateElementType {
    let updatedBlock = block;

    if (block.collapsedChildren) {
      const updatedChildren = block.collapsedChildren.map(migrateBlock);
      updatedBlock = {...updatedBlock, collapsedChildren: updatedChildren};
    }

    if (updatedBlock.type !== 'panel-grid' || !updatedBlock.metadata.runSets) {
      return updatedBlock;
    }

    const runSets = updatedBlock.metadata.runSets;
    const updatedRunSets = runSets.map(rs => {
      if (rs.filters == null) {
        return rs;
      }
      const migrated = migrateFilters(rs.filters, andOrEnabled);
      if (migrated === rs.filters) {
        return rs;
      }
      shouldUpdateSpec = true;
      return {...rs, filters: migrated};
    });

    if (!shouldUpdateSpec) {
      return updatedBlock;
    }

    return {
      ...updatedBlock,
      metadata: {
        ...updatedBlock.metadata,
        runSets: updatedRunSets,
      },
    };
  }

  const updatedBlocks = spec.blocks.map(migrateBlock);

  if (!shouldUpdateSpec) {
    return {spec, isFiltersMigrated: false};
  }

  return {
    spec: {
      ...spec,
      blocks: updatedBlocks,
    },
    isFiltersMigrated: true,
  };
}

/**
 * Apply the same logic as filtersFromJSON in parseSpecUtils.ts:
 * - v2 + flag on → keep as-is
 * - v2 + flag off → legacy empty
 * - legacy + flag on → migrate to v2
 * - legacy + flag off → keep as-is
 */
function migrateFilters(
  filters: FilterV2Root | FilterTypes.RootFilter<RunTypes.Key>,
  andOrEnabled: boolean
): FilterV2Root | FilterTypes.RootFilter<RunTypes.Key> {
  if (isFilterV2(filters)) {
    return andOrEnabled ? filters : Filter.EMPTY_FILTERS;
  }

  return andOrEnabled ? migrateLegacyFiltersToV2(filters) : filters;
}
